<template>
  <v-card-text class="card-content">
    <v-list dense class="snippet-list">
      <v-list-item
        v-for="kurztext in filteredKurztexte"
        :key="kurztext.id"
        class="snippet-item"
        rounded
      >
        <div class="d-flex align-center justify-space-between w-100">
          <div class="snippet-info flex-grow-1">
            <div class="d-flex align-center">
              <v-chip
                size="x-small"
                class="snippet-number mr-2"
                variant="outlined"
                color="success"
                title="Nummer"
              >
                {{ kurztext.nummer }}
              </v-chip>
              <v-list-item-title class="snippet-title text-truncate">
                {{ kurztext.bezeichnung || "Ohne Bezeichnung" }}
              </v-list-item-title>
            </div>
            <v-list-item-subtitle class="snippet-text text-truncate">
              {{ truncateText(kurztext.kurztext, 120) }}
            </v-list-item-subtitle>
          </div>
          <div class="snippet-actions ml-2">
            <v-btn
              icon
              size="x-small"
              variant="text"
              color="success"
              title="Kurztext kopieren"
              @click="copyToClipboard(kurztext.kurztext)"
            >
              <v-icon size="small">mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
      </v-list-item>

      <v-list-item v-if="filteredKurztexte.length === 0" disabled>
        <v-list-item-title class="text-center text-grey">
          Keine Kurztexte gefunden
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <div class="d-flex justify-end mt-2" v-if="showMoreButton">
      <v-btn
        text
        size="small"
        variant="text"
        color="success"
        class="show-more-button"
        @click="toggleShowAllKurztexte"
      >
        <v-icon size="small" class="mr-1">
          {{ showAllKurztexte ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
        {{ showAllKurztexte ? "Weniger anzeigen" : "Alle anzeigen" }}
      </v-btn>
    </div>
  </v-card-text>
</template>

<script setup>
import { inject, computed, ref, watch, nextTick } from "vue";
import { useToast } from "vue-toastification";

 
// Props hinzufügen, um Filterung zu unterstützen
const props = defineProps({
  filterQuery: {
    type: String,
    default: ''
  }
});
 

// Inject benötigte Daten und Funktionen
const globalKurztexte = inject("globalKurztexte");
const displayedKurztexte = inject("displayedKurztexte");
const showAllKurztexte = inject("showAllKurztexte");
const toggleShowAllKurztexte = inject("toggleShowAllKurztexte");
const truncateText = inject("truncateText");

// Gefilterte Kurztexte basierend auf Props und injizierten Daten
const filteredKurztexte = computed(() => {
  // Wenn filterQuery aus Props vorhanden ist, diese verwenden
  if (props.filterQuery) {
    const query = props.filterQuery.toLowerCase();
    return globalKurztexte.value.filter(
      item => item.kurztext.toLowerCase().includes(query) ||
              (item.nummer ? String(item.nummer).toLowerCase().includes(query) : false) ||
              (item.bezeichnung && item.bezeichnung.toLowerCase().includes(query))
    );
  }
  // Sonst die injizierten displayedKurztexte verwenden
  return displayedKurztexte.value;
});

// Nur "Mehr anzeigen"-Button zeigen, wenn sinnvoll
const showMoreButton = computed(() => {
  // Wenn explizit gefiltert wird, keinen Button zeigen
  if (props.filterQuery) return false;

  // Nur zeigen wenn es mehr Kurztexte gibt als standardmäßig angezeigt werden
  return globalKurztexte.value.length > 4;
});

// Hole die copyToClipboard-Funktion aus dem Parent oder erstelle sie hier
const providedCopyToClipboard = inject("copyToClipboard", null);
const toast = useToast();

// Fallback, wenn die Funktion nicht injiziert werden konnte
const copyToClipboard =
  providedCopyToClipboard ||
  ((text) => {
    try {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success(`Text in die Zwischenablage kopiert!`);
        })
        .catch((err) => {
          console.error("Fehler beim Kopieren in die Zwischenablage:", err);
          toast.error("Kopieren fehlgeschlagen");
        });
    } catch (err) {
      console.error("Fehler beim Kopieren in die Zwischenablage:", err);
      toast.error("Kopieren fehlgeschlagen");
    }
  });

// Dialog-Steuervariablen für den übergeordneten Dashboard-View
const showKurztextSearchDialog = inject('showKurztextSearchDialog', ref(false));
const showAddKurztextDialog = inject('showAddKurztextDialog', ref(false));

// Beobachten und auf Änderungen reagieren
watch(showKurztextSearchDialog, (newVal) => {
  if (newVal === true) {
    // Hier können wir die Suche direkt an den übergeordneten Dashboard-View weiterleiten
    // Der Dialog dort sollte sich öffnen
    nextTick(() => {
      showKurztextSearchDialog.value = false;
    });
  }
});

watch(showAddKurztextDialog, (newVal) => {
  if (newVal === true) {
    // Hier würde sich der Dialog zum Hinzufügen eines Kurztextes öffnen
    // Diese Implementierung hängt davon ab, wie das Hinzufügen im Dashboard gehandhabt wird
    nextTick(() => {
      showAddKurztextDialog.value = false;
    });
  }
});
</script>

<style scoped>
.card-content {
  padding: 16px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stats-header {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1),
    transparent
  );
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 16px !important;
}

.snippet-list {
  max-height: 350px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 4px;
  border: 0px solid rgba(255, 255, 255, 0.05);
}

.snippet-item {
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.snippet-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.snippet-number {
  min-width: 30px;
  display: flex;
  justify-content: center;
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.snippet-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
}

.snippet-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.snippet-actions {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.snippet-item:hover .snippet-actions {
  opacity: 1;
}

.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.show-more-button .v-icon {
  transition: transform 0.3s ease;
}

.show-more-button:hover .v-icon {
  transform: translateY(-2px);
}
</style>
